import { TRACKING_CONFIG } from '@intent/shared/tracking';
import type { EventQueue } from './event-queue';

/** Longest CSS selector we will build for a clicked element. */
const MAX_SELECTOR_DEPTH = 4;

/**
 * Translates browser activity into visitor events.
 *
 * Everything here is passive observation of the host page — no styles are
 * injected, no elements are modified, and every listener is registered
 * passively so scrolling stays smooth.
 */
export class Tracker {
  private pageEnteredAt = Date.now();
  private maxScrollPercentage = 0;
  private scrollThrottledAt = 0;
  private currentPath = window.location.pathname;
  private disposers: Array<() => void> = [];
  private started = false;

  constructor(private readonly queue: EventQueue) {}

  /** Seconds spent on the page currently being viewed. */
  get timeOnPage(): number {
    return Math.floor((Date.now() - this.pageEnteredAt) / 1_000);
  }

  start(): void {
    if (this.started) return;
    this.started = true;

    this.trackPageView();
    this.listenForScroll();
    this.listenForClicks();
    this.listenForExitIntent();
    this.listenForNavigation();
    this.listenForUnload();
  }

  stop(): void {
    for (const dispose of this.disposers) dispose();
    this.disposers = [];
    this.started = false;
  }

  /**
   * Registers a listener and records how to remove it. Typed against both
   * event maps because the tracker listens on `window` and `document` alike.
   */
  private on<K extends keyof (WindowEventMap & DocumentEventMap)>(
    target: Window | Document,
    type: K,
    handler: (event: (WindowEventMap & DocumentEventMap)[K]) => void,
    options?: AddEventListenerOptions,
  ): void {
    const listener = handler as EventListener;
    target.addEventListener(type, listener, options);
    this.disposers.push(() => target.removeEventListener(type, listener));
  }

  /** Emits a page view and resets the per-page counters. */
  trackPageView(): void {
    this.pageEnteredAt = Date.now();
    this.maxScrollPercentage = 0;
    this.currentPath = window.location.pathname;

    this.queue.enqueue('PAGE_VIEW', { title: document.title.slice(0, 300) });
  }

  private listenForScroll(): void {
    this.on(
      window,
      'scroll',
      () => {
        const now = Date.now();
        if (now - this.scrollThrottledAt < TRACKING_CONFIG.scrollThrottleMs) return;
        this.scrollThrottledAt = now;

        const percentage = currentScrollPercentage();
        // Only deepening the scroll is interesting; scrolling back up is not
        // a new signal and would double-report the same depth.
        if (percentage > this.maxScrollPercentage) {
          this.maxScrollPercentage = percentage;
          this.queue.enqueue('SCROLL', { scrollPercentage: percentage });
        }
      },
      { passive: true },
    );
  }

  private listenForClicks(): void {
    this.on(
      document,
      'click',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        this.queue.enqueue('CLICK', {
          selector: buildSelector(target),
          text: (target.textContent ?? '').trim().slice(0, 200) || undefined,
        });
      },
      { passive: true, capture: true },
    );
  }

  /**
   * Exit intent: the pointer leaving through the top of the viewport, which
   * usually means a reach for the address bar or tab strip.
   */
  private listenForExitIntent(): void {
    this.on(
      document,
      'mouseout',
      (event) => {
        const mouseEvent = event as MouseEvent;
        if (mouseEvent.relatedTarget !== null) return;
        if (mouseEvent.clientY > 0) return;

        this.queue.enqueue('MOUSE_LEAVE_WINDOW', { timeOnPage: this.timeOnPage });
      },
      { passive: true },
    );
  }

  /**
   * Single-page apps change the URL without reloading, so `history` is patched
   * to notice. The original methods are restored on `stop()`.
   */
  private listenForNavigation(): void {
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    const notify = (): void => {
      // The history API updates `location` synchronously, but frameworks often
      // render on a microtask; deferring keeps `document.title` in step.
      window.setTimeout(() => {
        if (window.location.pathname === this.currentPath) return;
        this.trackPageLeave();
        this.trackPageView();
      }, 0);
    };

    history.pushState = function patchedPushState(...args) {
      const result = originalPushState.apply(this, args);
      notify();
      return result;
    };

    history.replaceState = function patchedReplaceState(...args) {
      const result = originalReplaceState.apply(this, args);
      notify();
      return result;
    };

    this.disposers.push(() => {
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
    });

    this.on(window, 'popstate', notify);
  }

  private trackPageLeave(): void {
    this.queue.enqueue('PAGE_LEAVE', {
      timeOnPage: this.timeOnPage,
      scrollPercentage: this.maxScrollPercentage,
    });
  }

  /**
   * `pagehide` is used rather than `beforeunload`: it fires reliably on mobile
   * and is compatible with the back/forward cache.
   *
   * Note that no SESSION_END is emitted here — a page unload is usually just
   * navigation to the next page. Sessions are closed server-side once they go
   * idle, which is the only way to tell "left the page" from "left the site".
   */
  private listenForUnload(): void {
    const handleHide = (): void => {
      this.trackPageLeave();
      this.queue.flushOnUnload();
    };

    this.on(window, 'pagehide', handleHide);

    this.on(document, 'visibilitychange', () => {
      if (document.visibilityState === 'hidden') handleHide();
    });
  }
}

/** Scroll depth as a percentage of the scrollable area, clamped to 0–100. */
export function currentScrollPercentage(): number {
  const doc = document.documentElement;
  const scrollable = doc.scrollHeight - window.innerHeight;

  // A page shorter than the viewport is fully seen the moment it loads.
  if (scrollable <= 0) return 100;

  const ratio = (window.scrollY || doc.scrollTop) / scrollable;
  return Math.max(0, Math.min(100, Math.round(ratio * 100)));
}

/**
 * Builds a short, readable selector for a clicked element — enough for a
 * founder to recognise what was clicked, without serialising the DOM.
 */
export function buildSelector(element: Element): string {
  const parts: string[] = [];
  let current: Element | null = element;
  let depth = 0;

  while (current && depth < MAX_SELECTOR_DEPTH) {
    if (current.id) {
      parts.unshift(`#${current.id}`);
      break;
    }

    let part = current.tagName.toLowerCase();
    const className = typeof current.className === 'string' ? current.className.trim() : '';

    if (className) {
      const first = className.split(/\s+/)[0];
      if (first) part += `.${first}`;
    }

    parts.unshift(part);
    current = current.parentElement;
    depth += 1;
  }

  return parts.join(' > ').slice(0, 500);
}
